import { NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { get } from 'lodash';
import { IWidgetGlobalData, WIDGET_GLOBAL_DATA } from 'scw-digital-boards-widgets';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { EItemType } from '../../../pages/dashboard-content/widget-add-edit-dialog/widget-add-edit-dialog.interface';
import { ESnackbar, SnackbarService } from '../../../utilities/snackbar.service';
import { ActionDetailDialogComponent } from '../action-detail-dialog/action-detail-dialog.component';
import { CountdownManager } from '../countdown/countdown-manager.class';

export interface IAddActionDialogConfig {
  cancelButtonText: string;
  boardId: number | null;
  itemTypeId: EItemType | null;
  itemCategoryId: number | null;
  countdownManager?: CountdownManager;
}

export interface IItemFieldsFromUrl {
  name?: string;
  assignee?: string;
  boardId?: number;
  itemCategory?: number;
  itemType?: EItemType;
}

@Component({
  imports: [NgIf, MatDialogActions, MatButton],
  selector: 'app-add-action-dialog',
  standalone: true,
  template: `
    <div class="iframe-container">
      <iframe [src]="safeUrl" frameborder="0" width="100%" style="height: 80vh;" allow="camera;"></iframe>
    </div>
    <div *ngIf="!hideActions" mat-dialog-actions align="end">
      <button *ngIf="data.cancelButtonText" matButton="tonal" (click)="onDismiss()">
        {{ data.cancelButtonText }}
      </button>
    </div>
  `,
})
export class AddActionDialogComponent implements OnDestroy {
  public safeUrl: SafeResourceUrl;
  public hideActions = false;

  constructor(
    public dialogRef: MatDialogRef<AddActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAddActionDialogConfig,
    @Inject(WIDGET_GLOBAL_DATA) private globalData: IWidgetGlobalData,
    private readonly sanitizer: DomSanitizer,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly matDialog: MatDialog,
  ) {
    const fullUrl: string = this.buildIframeUrl(this.globalData.actionTrackerUrl, this.data);
    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
  }

  @HostListener('window:message', ['$event'])
  public handleMessage(event: MessageEvent): void {
    const origin: string = this.globalData.actionTrackerUrl;

    if (origin && !origin.includes(event.origin)) {
      return;
    }

    if (get(event.data, 'type') === 'DIALOG_CLOSED') {
      const key = get(event.data, 'payload.key') as string | undefined;

      if (key) {
        this.dialogRef.close({ key });

        const snackbarRef = this.snackbarService.open(
          this.translate.instant('system.message.itemCreatedSuccessfully', { itemId: key }),
          ESnackbar.success,
          15000,
          this.translate.instant('button.view'),
        );

        snackbarRef.onAction().subscribe(() => {
          this.openActionDetailDialog(key);
        });
      } else {
        this.dialogRef.close();
      }
    }

    if (get(event.data, 'type') === 'DIALOG_OPENED') {
      this.hideActions = true;
      this.cdr.detectChanges();
    }
  }

  private openActionDetailDialog(itemKey: string): void {
    this.matDialog.open(ActionDetailDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.close'),
        countdownManager: this.data.countdownManager,
        itemKey,
      },
      disableClose: true,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.large,
    });
  }

  private buildIframeUrl(baseUrl: string, config: IAddActionDialogConfig): string {
    const { boardId, itemTypeId, itemCategoryId } = config;

    const actionParams: IItemFieldsFromUrl = {};

    if (boardId) {
      actionParams.boardId = boardId;
    }

    if (itemTypeId) {
      actionParams.itemType = itemTypeId;
    }

    if (itemCategoryId) {
      actionParams.itemCategory = itemCategoryId;
    }

    if (Object.keys(actionParams).length === 0) {
      return `${baseUrl}/item/create`;
    }

    const jsonString: string = JSON.stringify(actionParams);
    const encodedFields: string = encodeURIComponent(jsonString);

    return `${baseUrl}/item/create?fields=${encodedFields}`;
  }

  public onDismiss(): void {
    this.dialogRef.close();
  }

  public ngOnDestroy(): void {
    window.removeEventListener('message', this.handleMessage.bind(this));
  }
}
